import { routeConstants } from "./constants";
import { getMeristemConfigValue } from "./meristemConfig";
import { getRouteId } from "./meristemContext";
import { getUserCurrency } from "./plans";
import { formatPrice } from "./priceUtils";
import { getURLParams } from "./urlParams";
import { isDirect, isEnLanguage, isUS } from "./userSegment";

const BLACK_FRIDAY_PROMO_CODE_3_MONTHS_OFF = "BLACKFRIDAY3";
const BLACK_FRIDAY_PROMO_CODE_6_MONTHS_OFF = "BLACKFRIDAY6";
const NEW_YEAR_PROMO_CODE_3_MONTHS_OFF = "NEWYEAR3";
const NEW_YEAR_PROMO_CODE_6_MONTHS_OFF = "NEWYEAR6";
export type HolidayPromo =
  | typeof BLACK_FRIDAY_PROMO_CODE_3_MONTHS_OFF
  | typeof BLACK_FRIDAY_PROMO_CODE_6_MONTHS_OFF
  | typeof NEW_YEAR_PROMO_CODE_3_MONTHS_OFF
  | typeof NEW_YEAR_PROMO_CODE_6_MONTHS_OFF;

const BLACK_FRIDAY_VALID_UTM_CONTENT = ["1863086"];
const BLACK_FRIDAY_VALID_UTM_SOURCE = [
  "360374",
  "312695",
  "59530",
  "121977",
  "332949",
  "1318738",
  "118767",
];

function isUserSegmentEligible() {
  return isEnLanguage();
}

function isRouteEligible() {
  const routeId = getRouteId();
  return [
    routeConstants.exsf01,
    routeConstants.exsf01d,
    routeConstants.affiliateTrialNPB,
  ].includes(routeId);
}

export function isNewYearPromotion() {
  const urlParams = getURLParams();
  const promo = urlParams?.promo;

  return (
    isUserSegmentEligible() &&
    isRouteEligible() &&
    ["3MonthNewYear2024", "6MonthNewYear2024"].includes(promo)
  );
}

export function isNewYearPromotionVar2() {
  const urlParams = getURLParams();
  const newYearPromo = urlParams?.newYearPromo;

  return isNewYearPromotion() && newYearPromo === "var2";
}

export function isBlackFridayPromotion() {
  const urlParams = getURLParams();
  const promo = urlParams?.promo;
  const utm_content = urlParams?.utm_content;
  const utm_source = urlParams?.utm_source;

  return (
    isUserSegmentEligible() &&
    isRouteEligible() &&
    (promo === "3MonthBFCM2023" ||
      (promo === "6MonthBFCM2023" && isUS()) ||
      BLACK_FRIDAY_VALID_UTM_CONTENT.includes(utm_content) ||
      BLACK_FRIDAY_VALID_UTM_SOURCE.includes(utm_source))
  );
}

export function isHolidayPromotion() {
  return isBlackFridayPromotion() || isNewYearPromotion();
}

export function getHolidayPromocode(): HolidayPromo {
  const forcedPlanDuration = getHolidayPromoForcedPlanDuration();
  if (isBlackFridayPromotion()) {
    return forcedPlanDuration === 12
      ? BLACK_FRIDAY_PROMO_CODE_6_MONTHS_OFF
      : BLACK_FRIDAY_PROMO_CODE_3_MONTHS_OFF;
  }

  return forcedPlanDuration === 12
    ? NEW_YEAR_PROMO_CODE_6_MONTHS_OFF
    : NEW_YEAR_PROMO_CODE_3_MONTHS_OFF;
}

export function isHolidayPromocode(
  initialPromocode: string
): initialPromocode is HolidayPromo {
  return [
    BLACK_FRIDAY_PROMO_CODE_3_MONTHS_OFF,
    BLACK_FRIDAY_PROMO_CODE_6_MONTHS_OFF,
    NEW_YEAR_PROMO_CODE_3_MONTHS_OFF,
    NEW_YEAR_PROMO_CODE_6_MONTHS_OFF,
  ].includes(initialPromocode);
}

export function canApplyHolidayPromocode(promocode: HolidayPromo) {
  if (promocode === BLACK_FRIDAY_PROMO_CODE_6_MONTHS_OFF && !isUS()) {
    return false;
  }

  // Allow users to redeem the promocode on direct in case they come back later
  return isUserSegmentEligible() && (isRouteEligible() || isDirect());
}

export function getHolidayPromoForcedPlanDuration(promocode?: HolidayPromo) {
  if (promocode) {
    return [
      BLACK_FRIDAY_PROMO_CODE_3_MONTHS_OFF,
      NEW_YEAR_PROMO_CODE_3_MONTHS_OFF,
    ].includes(promocode)
      ? 6
      : 12;
  }

  const urlParams = getURLParams();
  const promo = urlParams?.promo;
  if ((isUS() && promo === "6MonthBFCM2023") || promo === "6MonthNewYear2024") {
    return 12;
  }

  return 6;
}

// Hardcoded post-discount prices to avoid having
// to use an api request to load the forced plan before checkout
const priceMapping = {
  USD: {
    6: 159,
    12: 179,
  },
  CAD: {
    6: 129,
    12: 199,
  },
  NZD: {
    6: 169,
    12: 209,
  },
  AUD: {
    6: 149,
    12: 199,
  },
  GBP: {
    6: 59,
    12: 114,
  },
  EUR: {
    6: 99,
    12: 119,
  },
};

const priceMappingDecreased = {
  USD: {
    6: 139,
    12: 159,
  },
  CAD: {
    6: 129,
    12: 199,
  },
  NZD: {
    6: 169,
    12: 209,
  },
  AUD: {
    6: 149,
    12: 199,
  },
  GBP: {
    6: 59,
    12: 114,
  },
  EUR: {
    6: 99,
    12: 119,
  },
};
export function getHolidayPromoForcedPlanPrice() {
  const currency = getUserCurrency();
  const forcedPlanDuration = getHolidayPromoForcedPlanDuration();

  const mapping = getMeristemConfigValue("isPlanPriceDecreaseActive")
    ? priceMappingDecreased
    : priceMapping;

  const rawPrice = mapping[currency]?.[forcedPlanDuration];
  return formatPrice(rawPrice, currency);
}

const promocodeMapping = {
  USD: {
    6: "BFCMUSD3",
    12: "BFCMUSD6",
  },
  CAD: {
    6: "BFCMCAD3",
    12: "BFCMCAD6",
  },
  NZD: {
    6: "BFCMNZD3",
    12: "BFCMNZD6",
  },
  AUD: {
    6: "BFCMAUD3",
    12: "BFCMAUD6",
  },
  GBP: {
    6: "BFCMGBP3",
    12: "BFCMGBP6",
  },
  EUR: {
    6: "BFCMEUR3",
    12: "BFCMEUR6",
  },
};

export function getHolidayReplacementPromocode(
  promocode: HolidayPromo,
  currency: string
) {
  const planDuration = getHolidayPromoForcedPlanDuration(promocode);
  return promocodeMapping[currency]?.[planDuration];
}
